import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react';
import { Modal, Text } from '@vibe/core';

// Vibe 4 replaced the legacy Modal with a layout-based one (fixed size presets, a ModalBasicLayout that needs a
// header, and a ModalFooter that only takes button configs). AppModal keeps the legacy API and look on top of the
// new Modal: free width, 24px/32px padding, content that scrolls while the footer stays visible.

type AppModalWidth = 'default' | 'full-width' | number | string;

export type AppModalProps = {
  show: boolean;
  // Called without arguments. Vibe 4 passes the close event, which would leak into handlers like onClose(result?).
  onClose?: () => void;
  id?: string;
  width?: AppModalWidth;
  // No close on overlay click or Escape.
  alertDialog?: boolean;
  // Adds a 16px gap between header, content and footer.
  contentSpacing?: boolean;
  closeButtonAriaLabel?: string;
  className?: string;
  'data-testid'?: string;
  style?: CSSProperties;
  // Overrides the padding/layout of the inner body, e.g. { padding: 0 } for edge-to-edge content.
  bodyStyle?: CSSProperties;
  zIndex?: number;
  children: ReactNode;
};

function toCssWidth(width: AppModalWidth) {
  if (width === 'default') return '520px';
  if (width === 'full-width') return '100vw';
  return typeof width === 'number' ? `${width}px` : width;
}

export function AppModal({
  show,
  onClose,
  id,
  width = 'default',
  alertDialog = false,
  contentSpacing = false,
  closeButtonAriaLabel = 'Close',
  className,
  'data-testid': dataTestId,
  style,
  bodyStyle,
  zIndex,
  children,
}: AppModalProps) {
  const generatedId = useId();
  const modalRef = useRef<HTMLDivElement>(null);

  // Vibe 4 focuses the first focusable field on open. Inside clipped containers (Vibe's Box has overflow: hidden) the
  // browser then scrolls to that field and cuts off e.g. the top border of a large Dropdown. The legacy modal focused
  // an [autofocus] element or else the dialog itself; do the same, without scrolling.
  useEffect(() => {
    const modal = modalRef.current;
    if (!show || !modal) return;
    (modal.querySelector<HTMLElement>('[autofocus]') || modal).focus({ preventScroll: true });
  }, [show]);

  // When focus drops from an element in the modal to <body> (Vibe Buttons blur themselves on mouseup; fields get
  // disabled while a form submits), the focus lock re-checks on a timer. If the element is gone or disabled by then (a
  // Vibe Button remounts when `loading` starts), the lock moves focus to the nearest focusable element, usually the
  // close button, which then shows its tooltip. Park focus on the dialog right away (a microtask) so the lock has
  // nothing to restore.
  useEffect(() => {
    const modal = modalRef.current;
    if (!show || !modal) return;
    const onFocusOut = (event: FocusEvent) => {
      const target = event.target;
      if (event.relatedTarget || !(target instanceof HTMLElement) || !modal.contains(target)) return;
      queueMicrotask(() => {
        if (!document.activeElement || document.activeElement === document.body) modal.focus({ preventScroll: true });
      });
    };
    document.addEventListener('focusout', onFocusOut);
    return () => document.removeEventListener('focusout', onFocusOut);
  }, [show]);

  return (
    <Modal
      ref={modalRef}
      id={id || generatedId}
      autoFocus={false}
      show={show}
      onClose={() => onClose?.()}
      alertModal={alertDialog}
      closeButtonAriaLabel={closeButtonAriaLabel}
      className={className}
      data-testid={dataTestId}
      zIndex={zIndex}
      style={
        {
          // Sized by the body below, like the legacy dialog.
          '--modal-width': 'fit-content',
          '--modal-max-height': 'none',
          backgroundColor: 'var(--secondary-background-color)',
          ...style,
        } as CSSProperties
      }
    >
      {/* The legacy dialog applied width/max-width/max-height to its content box and added the padding on top
          (content-box), so width="900px" meant 900px of content. Reproduce that here. */}
      <div
        style={{
          boxSizing: 'content-box',
          width: toCssWidth(width),
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          gap: contentSpacing ? 'var(--space-16)' : undefined,
          minHeight: 0,
          overflow: 'auto',
          paddingBlock: 'var(--space-24)',
          paddingInline: 'var(--space-32)',
          ...bodyStyle,
        }}
      >
        {children}
      </div>
    </Modal>
  );
}

// Like the legacy ModalContent, a text1 Text (16px/22px, primary colour): content without its own font inherits it.
export function AppModalContent({ children, className, style }: { children?: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <Text type="text1" ellipsis={false} className={className} style={{ flexGrow: 1, minHeight: 0, overflow: 'auto', ...style }}>
      {children}
    </Text>
  );
}

export function AppModalFooter({ children, className, style }: { children?: ReactNode; className?: string; style?: CSSProperties }) {
  return (
    <div className={className} style={{ flexShrink: 0, marginBlockStart: 'var(--space-24)', ...style }}>
      {children}
    </div>
  );
}
